import { DateTime } from "luxon";
import queryString from "query-string";
import { ConfirmationInfo } from "../models/ConfirmationInfo";
import { EntranceTestBookingInfo } from "../models/EntranceTestBookingInfo";
import entranceTestBookingResponse from "../models/entranceTestBookingResponse";
import { GuardianInfo } from "../models/GuardianInfo";
import { InternalLoginDetails } from "../models/InternalLoginDetails";
import { InternalLoginDto } from "../models/InternalLoginDto";
import noEntranceTestBookingResponse from "../models/noEntranceTestBookingResponse";
import { PaymentInfo } from "../models/PaymentInfo";
import { SchoolInfo } from "../models/SchoolInfo";
import { StudentInfo } from "../models/StudentInfo";
import { TimeslotDto } from "../models/TimeslotDto";
import { VoucherInfo } from "../models/VoucherInfo";
import { VoucherResponseDto } from "../models/VoucherResponseDto";
import {
    createStudentRecordForEntranceTestFromInterface,
    createStudentRecordForNoEntranceTestFromInterface,
} from "../utils/combineInterfaceUtils";
import EMSXClient from "./EMSXClient";

export class EntryTestClient extends EMSXClient {
    protected getServicePath(): string {
        return "";
    }

    async getCapacityByDateTimeCentre(time: string, date: string, centreName: string) {
        const timeslot = DateTime.fromFormat(`${date} ${time}`, "yyyy-MM-dd h:mma").toFormat("yyyy-MM-dd HH:mm:ss");
        const queryParams = {
            centreName,
            timeslot,
        };
        const queryStringifyParams = queryString.stringify(queryParams);
        const requestUrl = `/efficient-software-search-capacity?${queryStringifyParams}`;
        return await super.get<any>(`${requestUrl}`);
    }

    async getSuburbs() {
        const requestUrl = `/efficient-software-suburb-search`;
        return await super.get<any>(`${requestUrl}`);
    }

    async getSchools() {
        const requestUrl = `/efficient-software-school-search`;
        return await super.get<any>(`${requestUrl}`);
    }

    async createStudentRecordWithEntranceTest(
        studentInfo: StudentInfo,
        schoolInfo: SchoolInfo,
        bookingInfo: EntranceTestBookingInfo,
        guardianInfo: GuardianInfo,
        confirmationInfo: ConfirmationInfo,
        paymentInfo: PaymentInfo,
        voucherInfo: VoucherInfo,
        internal: boolean,
    ): Promise<entranceTestBookingResponse> {
        const record = createStudentRecordForEntranceTestFromInterface(
            studentInfo,
            schoolInfo,
            bookingInfo,
            guardianInfo,
            confirmationInfo,
            paymentInfo,
            voucherInfo,
        );
        if (internal) {
            return await super.post<entranceTestBookingResponse>(
                "/efficient-software-create-student-record-with-test-internal",
                record,
            );
        } else {
            return await super.post<entranceTestBookingResponse>(
                "/efficient-software-create-student-record-with-test",
                record,
            );
        }
    }

    async createStudentRecordWithoutEntranceTest(
        studentInfo: StudentInfo,
        schoolInfo: SchoolInfo,
        guardianInfo: GuardianInfo,
        confirmationInfo: ConfirmationInfo,
    ) {
        const record = createStudentRecordForNoEntranceTestFromInterface(
            studentInfo,
            schoolInfo,
            guardianInfo,
            confirmationInfo,
        );
        return await super.post<noEntranceTestBookingResponse>(
            "/efficient-software-create-student-record-without-test",
            record,
        );
    }

    async getTimeslots(date: string, centreName: string) {
        return await super.get<TimeslotDto[]>(
            `/efficient-software-timeslot?${queryString.stringify({ date: date, centreName: centreName })}`,
        );
    }

    async checkForVoucherCode(voucherCode: string) {
        const queryStringifyParams = queryString.stringify({ voucherCode });
        return await super.get<VoucherResponseDto>(`/efficient-software-voucher-check?${queryStringifyParams}`);
    }

    async checkForInternalUser(internalLoginDetails: InternalLoginDetails) {
        return await super.post<InternalLoginDto>("/efficient-internal-login", internalLoginDetails);
    }

    async getSubjectNames() {
        return await super.get<string[]>(`/efficient-subject-names`);
    }

    async getEntrytestSubjectNames() {
        return await super.get<string[]>(`/efficient-entrytest-subject-names`);
    }
}

const entryTestClient = new EntryTestClient();
export default entryTestClient;
